<script setup lang="ts">
// Shared click-to-copy for human-facing IDs (SKY- item IDs, SKC- container labels, PO numbers).
// Renders the value (slot content, or the `value` prop) with the caller's own styling, followed by
// a small copy icon button. The button stops click propagation so it never triggers a row click or
// a wrapping link. Usage:
//   <CopyId :value="item.skylarkId" class="font-mono" />
//   <CopyId :value="po.poNumber"><span class="font-semibold">{{ po.poNumber }}</span></CopyId>
import { computed, onBeforeUnmount, ref } from 'vue'
import { Copy, Check } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  // The text copied to the clipboard (and shown when no default slot is given).
  value: string | null | undefined
  // Icon size in px.
  size?: number
  // Hide the value text and render only the icon (for when the caller renders the value elsewhere).
  iconOnly?: boolean
}>(), {
  size: 11,
  iconOnly: false,
})

const copied = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

const label = computed(() => `Copy ${props.value ?? ''}`.trim())

async function writeClipboard(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the textarea fallback (non-secure context, permission denied)
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '0'
    ta.style.left = '-9999px'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

async function copy(e: Event) {
  e.stopPropagation()
  e.preventDefault()
  if (!props.value) return
  const ok = await writeClipboard(props.value)
  if (!ok) return
  copied.value = true
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { copied.value = false }, 1500)
}

onBeforeUnmount(() => { if (timer) clearTimeout(timer) })
</script>

<template>
  <span v-if="value" class="inline-flex items-center gap-1 align-baseline">
    <template v-if="!iconOnly"><slot>{{ value }}</slot></template>
    <button
      type="button"
      class="inline-flex items-center justify-center shrink-0 p-0.5 rounded transition-colors"
      :class="copied ? 'text-green-500' : 'text-ink-disabled hover:text-ink-secondary'"
      :title="copied ? 'Copied' : label"
      :aria-label="label"
      @click.stop="copy"
      @mousedown.stop
    >
      <Check v-if="copied" :size="size" />
      <Copy v-else :size="size" />
    </button>
  </span>
  <span v-else><slot /></span>
</template>
